import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const Ctx = createContext({ open: () => {} });
export const useLightbox = () => useContext(Ctx);

export function LightboxProvider({ children }) {
  const [state, setState] = useState(null);
  const open = useCallback((images, index = 0) => setState({ images, index }), []);
  const close = () => setState(null);

  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (state.images.length > 1 && e.key === "ArrowRight")
        setState((s) => s && { ...s, index: (s.index + 1) % s.images.length });
      if (state.images.length > 1 && e.key === "ArrowLeft")
        setState((s) => s && { ...s, index: (s.index - 1 + s.images.length) % s.images.length });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state]);

  return (
    <Ctx.Provider value={{ open }}>
      {children}
      <AnimatePresence>
        {state && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="lightbox"
            onClick={close}
            className="fixed inset-0 z-[75] flex items-center justify-center bg-ink/95 backdrop-blur-sm"
          >
            <button
              data-testid="lightbox-close"
              aria-label="Close"
              onClick={close}
              className="absolute right-5 top-5 z-10 border border-white/20 p-3 text-white transition-colors hover:border-brand hover:bg-brand"
            >
              <X size={18} />
            </button>

            {state.images.length > 1 && (
              <>
                <button
                  data-testid="lightbox-prev"
                  aria-label="Previous photo"
                  onClick={(e) => {
                    e.stopPropagation();
                    setState((s) => s && { ...s, index: (s.index - 1 + s.images.length) % s.images.length });
                  }}
                  className="absolute left-4 z-10 flex h-11 w-11 items-center justify-center border border-white/20 bg-ink/70 text-white transition-colors hover:border-brand hover:bg-brand lg:left-8"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  data-testid="lightbox-next"
                  aria-label="Next photo"
                  onClick={(e) => {
                    e.stopPropagation();
                    setState((s) => s && { ...s, index: (s.index + 1) % s.images.length });
                  }}
                  className="absolute right-4 z-10 flex h-11 w-11 items-center justify-center border border-white/20 bg-ink/70 text-white transition-colors hover:border-brand hover:bg-brand lg:right-8"
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}

            <motion.img
              key={state.index}
              src={state.images[state.index]}
              alt="Enlarged view"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-image"
              className="max-h-[86vh] max-w-[92vw] object-contain"
            />

            {state.images.length > 1 && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-ink/70 px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] text-slate-200">
                {String(state.index + 1).padStart(2, "0")} / {String(state.images.length).padStart(2, "0")}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Ctx.Provider>
  );
}
